<script setup lang="ts">
import { useCandidature } from '~/composables/useCandidature'
import type { Formation } from '~/composables/useFormations'

const props = defineProps<{
  formations: Formation[]
  nextSession: string
}>()

const formationsRef = computed(() => props.formations)
const {
  form,
  motivationMode,
  cvFile,
  motivationFile,
  attachments,
  rgpdAccepted,
  subscribeNewsletter,
  isLoading,
  successMessage,
  errorMessage,
  fileSizeError,
  isSubmitted,
  fileStatuses,
  isScanningOrError,
  currentStep,
  stepsCount,
  stepTitles,
  selectedFormation,
  isStep1Valid,
  isStep2Valid,
  isStep3Valid,
  isStep4Valid,
  addAttachment,
  removeAttachment,
  handleFileChange,
  handleAttachmentChange,
  nextStep,
  prevStep,
  removeFile,
  submitApplication,
  isValidEmail,
  isValidPhone
} = useCandidature(formationsRef)
</script>

<template>
  <section id="candidature-form" class="py-section-py bg-background relative">
    <div class="container mx-auto px-container-px">
      <UiSectionHeader 
        align="left"
        titlePrefix="Déposez votre"
        titleHighlight="Candidature"
        subtitle="Remplissez le formulaire ci-dessous pour postuler. La candidature est gratuite et sans engagement."
      />

      <div class="max-w-4xl mx-auto mt-16">
        <div class="bg-oxo-card-bg/10 border border-oxo-border rounded-3xl overflow-hidden shadow-2xl backdrop-blur-xl">
          
          <!-- Success Message -->
          <div v-if="isSubmitted" class="p-12 text-center animate-fade-in-right">
            <div class="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 text-4xl mx-auto mb-8">
              <i class="fas fa-check" aria-hidden="true"></i>
            </div>
            <h3 class="font-display font-bold text-3xl text-foreground mb-4">Candidature Reçue !</h3>
            <p class="text-foreground-dim mb-8">
              Merci {{ form.firstName }}. Votre dossier a été transmis à notre équipe pédagogique.
            </p>
            <button @click="isSubmitted = false" class="btn-step-next">
              Envoyer une autre candidature
            </button>
          </div>

          <!-- Form Stepper -->
          <template v-else>
            <div class="bg-background/50 border-b border-oxo-border p-8">
              <div class="flex justify-between items-center relative">
                <!-- Progress Line -->
                <div class="absolute top-1/2 left-0 w-full h-0.5 bg-oxo-border -translate-y-1/2 z-0"></div>
                <div 
                  class="absolute top-1/2 left-0 h-0.5 bg-oxo-red -translate-y-1/2 z-0 transition-all duration-500"
                  :style="{ width: `${((currentStep - 1) / (stepsCount - 1)) * 100}%` }"
                ></div>

                <!-- Steps -->
                <div v-for="n in stepsCount" :key="n" class="relative z-10 flex flex-col items-center">
                  <div 
                    class="w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 border-2"
                    :class="[
                      currentStep === n ? 'bg-oxo-red border-oxo-red text-white scale-125 shadow-lg shadow-oxo-red/20' : 
                      currentStep > n ? 'bg-oxo-red border-oxo-red text-white' : 'bg-background border-oxo-border text-foreground-dim'
                    ]"
                  >
                    <i v-if="currentStep > n" class="fas fa-check text-xs" aria-hidden="true"></i>
                    <span v-else>{{ n }}</span>
                  </div>
                  <span class="absolute top-12 text-[10px] font-black uppercase tracking-widest whitespace-nowrap hidden sm:block"
                    :class="currentStep >= n ? 'text-oxo-red' : 'text-foreground-dim'"
                  >
                    {{ stepTitles[n-1] }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Form Content -->
            <form @submit.prevent="submitApplication" class="p-8 sm:p-12">
              
              <!-- STEP 1: IDENTITY -->
              <div v-if="currentStep === 1" class="space-y-6 animate-fade-in-right">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-8 h-8 rounded-lg bg-oxo-red/10 flex items-center justify-center text-oxo-red text-sm">
                    <i class="fas fa-user" aria-hidden="true"></i>
                  </div>
                  <h3 class="font-display font-bold text-xl text-foreground">Informations Personnelles</h3>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label for="lastname" class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Nom *</label>
                    <input v-model="form.lastName" type="text" id="lastname" required 
                      class="form-input-oxo"
                      placeholder="Votre nom"
                    />
                  </div>
                  
                  <div class="space-y-2">
                    <label for="firstname" class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Prénom *</label>
                    <input v-model="form.firstName" type="text" id="firstname" required 
                      class="form-input-oxo"
                      placeholder="Votre prénom"
                    />
                  </div>

                  <div class="space-y-2">
                    <label for="email" class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Email *</label>
                    <input v-model="form.email" type="email" id="email" required 
                      class="form-input-oxo"
                      :class="{ 'border-red-500! text-red-600': form.email && !isValidEmail(form.email), 'border-green-500!': form.email && isValidEmail(form.email) }"
                      placeholder="votre@email.com"
                    />
                    <p v-if="form.email && !isValidEmail(form.email)" class="text-[10px] text-red-500 font-bold mt-1">
                        Format email invalide (ex: exemple@domaine.com)
                    </p>
                  </div>

                  <div class="space-y-2">
                    <label for="phone" class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Téléphone *</label>
                    <input v-model="form.phone" type="tel" id="phone" required 
                      class="form-input-oxo"
                      :class="{ 'border-red-500! text-red-600': form.phone && !isValidPhone(form.phone), 'border-green-500!': form.phone && isValidPhone(form.phone) }"
                      placeholder="06 12 34 56 78"
                      title="Veuillez entrer un numéro de téléphone valide (ex: 06 12 34 56 78 ou +33 6 12 34 56 78)"
                    />
                    <p v-if="form.phone && !isValidPhone(form.phone)" class="text-[10px] text-red-500 font-bold mt-1">
                        Numéro invalide (10 chiffres ou format international +33...)
                    </p>
                  </div>
                </div>

                <div class="flex justify-end pt-6">
                  <AppButton type="button" @click="nextStep" :disabled="!isStep1Valid" variant="secondary" size="md">
                    Continuer <i class="fas fa-arrow-right ml-2" aria-hidden="true"></i>
                  </AppButton>
                </div>
              </div>

              <!-- STEP 2: PROJECT -->
              <div v-if="currentStep === 2" class="space-y-6 animate-fade-in-right">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-8 h-8 rounded-lg bg-oxo-red/10 flex items-center justify-center text-oxo-red text-sm">
                    <i class="fas fa-graduation-cap" aria-hidden="true"></i>
                  </div>
                  <h3 class="font-display font-bold text-xl text-foreground">Votre Projet</h3>
                </div>

                <div class="space-y-2">
                  <label for="formation" class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Formation souhaitée *</label>
                  <div class="relative">
                    <select v-model="form.formationSlug" id="formation" required 
                      class="form-input-oxo appearance-none cursor-pointer pr-12"
                    >
                      <option value="" disabled class="bg-white dark:bg-oxo-card">Sélectionnez une formation</option>
                      <option v-for="fmt in formations" :key="fmt.slug" :value="fmt.slug" class="bg-white dark:bg-oxo-card">
                        {{ fmt.title }} ({{ fmt.duration }})
                      </option>
                    </select>
                    <div class="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-dim">
                      <i class="fas fa-chevron-down" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>

                <div class="space-y-2">
                  <label for="portfolio" class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Lien Portfolio / ArtStation / GitHub</label>
                  <input v-model="form.portfolioUrl" type="url" id="portfolio" 
                    class="form-input-oxo"
                    placeholder="https://..."
                  />
                  <p class="text-[10px] text-foreground-dim italic">Optionnel mais fortement recommandé pour les profils créatifs et techniques.</p>
                </div>

                <div class="flex justify-between pt-6">
                  <AppButton type="button" @click="prevStep" variant="outline" size="md">
                    <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i> Précédent
                  </AppButton>
                  <AppButton type="button" @click="nextStep" :disabled="!isStep2Valid" variant="secondary" size="md">
                    Continuer <i class="fas fa-arrow-right ml-2" aria-hidden="true"></i>
                  </AppButton>
                </div>
              </div>

              <!-- STEP 3: DOCUMENTS -->
              <div v-if="currentStep === 3" class="space-y-6 animate-fade-in-right">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-8 h-8 rounded-lg bg-oxo-red/10 flex items-center justify-center text-oxo-red text-sm">
                    <i class="fas fa-file-upload" aria-hidden="true"></i>
                  </div>
                  <h3 class="font-display font-bold text-xl text-foreground">Documents & Motivation</h3>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <!-- CV -->
                  <div class="space-y-2">
                    <label class="text-sm font-bold text-foreground-dim uppercase tracking-wider">CV (PDF) *</label>
                    <div class="relative">
                      <!-- Show file name if already selected -->
                      <div v-if="cvFile" class="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30 mb-2 group">
                        <i class="fas fa-file-pdf text-oxo-red text-lg" aria-hidden="true"></i>
                        <span class="text-sm text-foreground font-medium truncate flex-1">{{ cvFile.name }}</span>
                        <span class="text-[10px] text-green-500 font-bold uppercase mr-2">Prêt</span>
                        <button type="button" @click="removeFile('cv')" class="opacity-70 group-hover:opacity-100 hover:text-red-500 transition-all p-1" title="Supprimer">
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                      <input v-else type="file" id="cv" ref="cvInput" accept=".pdf" required @change="handleFileChange($event, 'cv')"
                        class="form-file-oxo"
                      />
                      <div v-if="fileStatuses['cv'] && !cvFile" class="mt-2 text-sm font-bold flex items-center gap-2">
                        <span v-if="fileStatuses['cv'] === 'scanning'" class="text-oxo-blue"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Analyse...</span>
                        <span v-if="fileStatuses['cv'] === 'ok'" class="text-green-500"><i class="fas fa-check-circle" aria-hidden="true"></i> Prêt</span>
                        <span v-if="fileStatuses['cv'] === 'error'" class="text-red-500"><i class="fas fa-times-circle" aria-hidden="true"></i> Erreur</span>
                      </div>
                    </div>
                  </div>

                  <!-- Motivation -->
                  <div class="space-y-2">
                    <label class="text-sm font-bold text-foreground-dim uppercase tracking-wider">Lettre de Motivation *</label>
                    <div class="flex gap-4 mb-3">
                      <button type="button" @click="motivationMode = 'write'" class="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full transition-all" :class="motivationMode === 'write' ? 'bg-oxo-red text-white' : 'bg-oxo-card-bg/20 text-foreground-dim'">Rédiger</button>
                      <button type="button" @click="motivationMode = 'upload'" class="text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full transition-all" :class="motivationMode === 'upload' ? 'bg-oxo-red text-white' : 'bg-oxo-card-bg/20 text-foreground-dim'">Télécharger</button>
                    </div>
                    
                    <div v-if="motivationMode === 'write'">
                      <textarea 
                          v-model="form.message" 
                          rows="10"
                          placeholder="Exprimez votre passion pour le jeu vidéo et pourquoi vous souhaitez rejoindre l'école OXO..."
                          class="form-input-oxo resize-none leading-relaxed min-h-[250px]"
                      ></textarea>
                    </div>
                    <div v-else>
                      <!-- Show file name if already selected -->
                      <div v-if="motivationFile" class="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30 group">
                        <i class="fas fa-file-alt text-oxo-red text-lg" aria-hidden="true"></i>
                        <span class="text-sm text-foreground font-medium truncate flex-1">{{ motivationFile.name }}</span>
                        <span class="text-[10px] text-green-500 font-bold uppercase mr-2">Prêt</span>
                        <button type="button" @click="removeFile('motivation')" class="opacity-70 group-hover:opacity-100 hover:text-red-500 transition-all p-1" title="Supprimer">
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                      <input v-else type="file" ref="motivationInput" accept=".pdf,.doc,.docx" @change="handleFileChange($event, 'motivation')"
                        class="form-file-oxo"
                      />
                    </div>
                    <div v-if="fileStatuses['motivation'] && !motivationFile" class="text-sm font-bold flex items-center gap-2">
                        <span v-if="fileStatuses['motivation'] === 'scanning'" class="text-oxo-blue"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Analyse...</span>
                        <span v-if="fileStatuses['motivation'] === 'ok'" class="text-green-500"><i class="fas fa-check-circle" aria-hidden="true"></i> Prêt</span>
                    </div>
                  </div>
                </div>

                <!-- Shared Files -->
                <div class="pt-6 border-t border-oxo-border">
                  <div class="flex justify-between items-center mb-4">
                    <h4 class="text-sm font-bold text-foreground">Fichiers Complémentaires</h4>
                    <button type="button" @click="addAttachment" class="text-[10px] font-bold uppercase tracking-widest text-oxo-red hover:underline">
                      + Ajouter un fichier
                    </button>
                  </div>
                  
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div v-for="(att, index) in attachments" :key="index" class="p-4 rounded-xl bg-background/50 border border-oxo-border relative group">
                      <button type="button" @click="removeAttachment(index)" class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i class="fas fa-times text-[10px]" aria-hidden="true"></i>
                      </button>
                      
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="space-y-1">
                          <label class="text-[10px] font-bold text-foreground-dim uppercase">Type</label>
                          <input v-model="att.type" type="text" placeholder="Ex: Bulletin" class="form-input-oxo-small" />
                        </div>
                        <div class="space-y-1">
                          <label class="text-[10px] font-bold text-foreground-dim uppercase">Fichier</label>
                          <!-- Show file name if already selected -->
                          <div v-if="att.file" class="flex items-center gap-2 p-2 rounded-sm bg-green-500/10 border border-green-500/30 group/file">
                            <i class="fas fa-paperclip text-oxo-red text-xs" aria-hidden="true"></i>
                            <span class="text-[10px] text-foreground font-medium truncate flex-1">{{ att.file.name }}</span>
                            <span class="text-[10px] text-green-500 font-bold">OK</span>
                            <button type="button" @click="removeFile(`attachment_${index}`)" class="ml-2 hover:text-red-500 transition-colors" title="Retirer ce fichier">
                                <i class="fas fa-times text-[10px]" aria-hidden="true"></i>
                            </button>
                          </div>
                          <input v-else type="file" @change="handleAttachmentChange($event, index)" class="text-[10px] text-foreground-dim block w-full file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 file:text-[10px] file:font-bold file:bg-oxo-red/10 file:text-oxo-red hover:file:bg-oxo-red/20" />
                          <div v-if="fileStatuses[`attachment_${index}`] && !att.file" class="mt-1 text-[10px] font-bold flex items-center gap-1">
                            <span v-if="fileStatuses[`attachment_${index}`] === 'scanning'" class="text-oxo-blue">Scan...</span>
                            <span v-if="fileStatuses[`attachment_${index}`] === 'ok'" class="text-green-500">OK</span>
                            <span v-if="fileStatuses[`attachment_${index}`] === 'error'" class="text-red-500">Error</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p v-if="attachments.length === 0" class="text-[10px] text-foreground-dim italic">Bulletins, diplômes ou réalisations.</p>
                </div>

                <div v-if="fileSizeError" class="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 font-bold text-sm">
                   <i class="fas fa-exclamation-triangle mr-2" aria-hidden="true"></i> {{ fileSizeError }}
                   <p class="text-xs font-normal mt-2 opacity-80">Limite : 2 Mo par fichier, 15 Mo au total.</p>
                </div>
                <div v-if="successMessage" class="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 font-bold text-sm">
                   <i class="fas fa-check-circle mr-2" aria-hidden="true"></i> {{ successMessage }}
                </div>
                <div v-if="errorMessage" class="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm">
                   <i class="fas fa-exclamation-circle mr-2" aria-hidden="true"></i> {{ errorMessage }}
                </div>

                <div class="flex flex-col sm:flex-row gap-4 pt-6">
                  <AppButton type="button" @click="prevStep" variant="outline" size="md" class="flex-1 justify-center">
                    <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i> Précédent
                  </AppButton>
                  <AppButton type="button" @click="nextStep" :disabled="!isStep3Valid" variant="secondary" size="md" class="flex-2 justify-center">
                    Vérifier ma candidature <i class="fas fa-eye ml-2" aria-hidden="true"></i>
                  </AppButton>
                </div>
              </div>

              <!-- STEP 4: RECAP & VALIDATION -->
              <div v-if="currentStep === 4" class="space-y-8 animate-fade-in-right">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-8 h-8 rounded-lg bg-oxo-red/10 flex items-center justify-center text-oxo-red text-sm">
                    <i class="fas fa-clipboard-check" aria-hidden="true"></i>
                  </div>
                  <h3 class="font-display font-bold text-xl text-foreground">Récapitulatif & Validation</h3>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-background/30 p-6 rounded-xl border border-oxo-border/50">
                  <div class="space-y-4">
                    <div>
                      <h4 class="text-[10px] font-black uppercase tracking-widest text-oxo-red mb-1">Votre Identité</h4>
                      <p class="text-sm font-bold text-foreground">{{ form.firstName }} {{ form.lastName }}</p>
                      <p class="text-xs text-foreground-dim">{{ form.email }} • {{ form.phone }}</p>
                    </div>
                    <div>
                      <h4 class="text-[10px] font-black uppercase tracking-widest text-oxo-red mb-1">Votre Projet</h4>
                      <p class="text-sm font-bold text-foreground">{{ selectedFormation?.title || 'Non sélectionnée' }}</p>
                      <p v-if="form.portfolioUrl" class="text-xs text-foreground-dim truncate max-w-full">
                        <i class="fas fa-link mr-1" aria-hidden="true"></i> {{ form.portfolioUrl }}
                      </p>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <div>
                      <h4 class="text-[10px] font-black uppercase tracking-widest text-oxo-red mb-1">Documents Joints</h4>
                      <ul class="space-y-1">
                        <li class="text-xs flex items-center gap-2 text-foreground-dim">
                          <i class="fas fa-file-pdf text-oxo-red" aria-hidden="true"></i>
                          CV : <span class="text-foreground italic">{{ cvFile?.name }}</span>
                        </li>
                        <li class="text-xs flex items-center gap-2 text-foreground-dim">
                          <i class="fas fa-file-alt text-oxo-red" aria-hidden="true"></i>
                          Motivation : <span v-if="motivationMode === 'write'" class="text-foreground">Saisie manuellement</span>
                          <span v-else class="text-foreground italic">{{ motivationFile?.name }}</span>
                        </li>
                        <li v-for="(att, idx) in attachments" :key="idx" class="text-xs flex items-center gap-2 text-foreground-dim">
                          <i class="fas fa-paperclip text-oxo-red" aria-hidden="true"></i>
                          {{ att.type || 'Autre' }} : <span class="text-foreground italic">{{ att.file?.name }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div class="p-6 bg-oxo-red/5 rounded-xl border border-oxo-red/20 space-y-4">
                  <div class="flex gap-4">
                    <div class="pt-1">
                      <input 
                        type="checkbox" 
                        id="rgpd" 
                        v-model="rgpdAccepted" 
                        required
                        class="w-5 h-5 rounded-sm border-oxo-border text-oxo-red focus:ring-oxo-red cursor-pointer"
                      />
                    </div>
                    <label for="rgpd" class="text-sm text-foreground leading-relaxed cursor-pointer select-none">
                      <span class="font-bold">Accord de traitement des données *</span><br/>
                      J'accepte d'être recontacté par l'école OXO concernant ma candidature. Mes données seront conservées de manière sécurisée dans le but unique du suivi de mon admission. Elles ne seront en aucun cas partagées avec des tiers et resteront uniquement accessibles aux membres de l'administration OXO.
                    </label>
                  </div>

                  <!-- Newsletter Opt-in -->
                  <div class="flex gap-4 border-t border-oxo-red/10 pt-4">
                    <div class="pt-1">
                      <input 
                        type="checkbox" 
                        id="newsletter-candidature" 
                        v-model="subscribeNewsletter" 
                        class="w-5 h-5 rounded-sm border-oxo-border text-oxo-red focus:ring-oxo-red cursor-pointer"
                      />
                    </div>
                    <label for="newsletter-candidature" class="text-sm text-foreground leading-relaxed cursor-pointer select-none">
                      <i class="fas fa-envelope text-oxo-red mr-1" aria-hidden="true"></i>
                      Je souhaite recevoir la newsletter OXO pour rester informé(e) des actualités, événements et opportunités.
                    </label>
                  </div>

                  <div class="text-[10px] text-foreground-dim leading-relaxed border-t border-oxo-red/10 pt-4">
                    <p>
                      Conformément au <strong class="text-foreground">RGPD</strong>, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. 
                      Pour exercer ce droit ou retirer votre consentement, contactez-nous à <a href="mailto:secretariat@oxonarbonne.fr" class="text-oxo-red hover:underline">secretariat@oxonarbonne.fr</a>.
                    </p>
                  </div>
                </div>

                <div v-if="successMessage" class="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 font-bold text-sm">
                   <i class="fas fa-check-circle mr-2" aria-hidden="true"></i> {{ successMessage }}
                </div>
                <div v-if="errorMessage" class="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm">
                   <i class="fas fa-exclamation-circle mr-2" aria-hidden="true"></i> {{ errorMessage }}
                </div>

                <div class="flex flex-col sm:flex-row gap-4 pt-6">
                  <AppButton type="button" @click="prevStep" variant="outline" size="md" class="flex-1 justify-center">
                    <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i> Précédent
                  </AppButton>
                  <AppButton type="submit" :disabled="isLoading || isScanningOrError || !isStep4Valid" variant="primary" size="md" :loading="isLoading" class="flex-2 justify-center">
                    <span v-if="!isLoading">Confirmer & Envoyer</span>
                    <i v-if="!isLoading" class="fas fa-paper-plane ml-2" aria-hidden="true"></i>
                  </AppButton>
                </div>
              </div>
            </form>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@reference "../../assets/css/tailwind.css";

.form-input-oxo {
  @apply w-full px-4 py-3 rounded-lg bg-background border border-oxo-border text-foreground focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-all duration-300;
}

.form-input-oxo-small {
  @apply w-full px-3 py-2 rounded bg-background border border-oxo-border text-sm outline-hidden focus:border-oxo-red text-foreground transition-all duration-300;
}

.form-file-oxo {
  @apply w-full px-4 py-3 rounded-lg bg-background border border-oxo-border text-foreground-dim focus:outline-hidden transition-all duration-300;
}

.form-file-oxo::file-selector-button {
  margin-right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  border-width: 0;
  font-size: 0.875rem;
  font-weight: 700;
  background-color: rgba(230, 0, 0, 0.1);
  color: #e60000;
  cursor: pointer;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

.form-file-oxo::file-selector-button:hover {
  background-color: rgba(230, 0, 0, 0.2);
}



@keyframes fade-in-right {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in-right {
  animation: fade-in-right 0.5s ease-out forwards;
}
</style>
